import PropTypes from 'prop-types'
import Markdown from 'react-markdown'

import {
  isSkipperReport,
  readDisplayMd,
  readResolvedSpec
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'
import {
  SKIPPER_REPORT_FEATURE,
  ANALYSIS_SETTINGS_PENDING
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportsConst'
import { openGuidedReportsChat } from '@aytm/app/stats/stores/guidedReportsEntry'

// components
import { Button } from 'primereact/button'

import UnlockLink from '@aytm/app/stats/page/sidebar/reports/components/SkipperUnlockLink'
import ReportPopupSection from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupSection'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportPopup.module.scss'

const List = ({ items }) =>
  0 < items.length ? (
    <div className={style.list}>
      {items.map((text, i) => (
        <div className={style.item} key={i}>
          {text}
        </div>
      ))}
    </div>
  ) : null

List.propTypes = { items: PropTypes.array }

// A queued report carries neither the panel string nor a resolved spec, so it lands on the same
// empty branch as one whose failed generation dropped the snapshot — but nothing has failed there.
// The status alone tells the two apart: a failure settles the row for viewing rather than putting
// it back, and the list withholds the status on every terminal outcome.
const isAwaitingGeneration = report => ['draft', 'generating'].includes(report.get('status'))

const hasResolvedSpec = spec =>
  Boolean(
    spec.objectives.length ||
      spec.researchQuestions.length ||
      spec.kpis.length ||
      null != spec.confidenceLevel ||
      spec.methodology
  )

const ResolvedSpecRows = ({ spec }) => {
  const confidence =
    null == spec.confidenceLevel
      ? null
      : spec.confidenceLevel <= 1
      ? `${Math.round(spec.confidenceLevel * 100)}%`
      : `${spec.confidenceLevel}`

  return (
    <>
      {spec.objectives.length ? (
        <div className={style['description-content-section-body-content-row']}>
          <b>Research objective</b>
          <List items={spec.objectives} />
        </div>
      ) : null}

      {spec.researchQuestions.length ? (
        <div className={style['description-content-section-body-content-row']}>
          <b>Research questions</b>
          <List items={spec.researchQuestions} />
        </div>
      ) : null}

      {spec.kpis.length ? (
        <div className={style['description-content-section-body-content-row']}>
          <b>KPIs</b>
          <List items={spec.kpis} />
        </div>
      ) : null}

      {confidence ? (
        <div className={style['description-content-section-body-content-row']}>
          <b>Confidence level</b>
          <div className={style.value}>{confidence}</div>
        </div>
      ) : null}

      {spec.methodology ? (
        <div className={style['description-content-section-body-content-row']}>
          <b>Methodology</b>
          <div className={style.value}>{spec.methodology}</div>
        </div>
      ) : null}
    </>
  )
}

ResolvedSpecRows.propTypes = { spec: PropTypes.object }

// Whether the section has anything to say: a plan, a resolved spec, or a generation still to
// come. A report that kept none of them - a failed generation drops the snapshot - gets no
// section at all rather than a header opening onto an explanation of its own emptiness.
export const hasAnalysisSettings = report =>
  Boolean(readDisplayMd(report)) || hasResolvedSpec(readResolvedSpec(report)) || isAwaitingGeneration(report)

// Read-only "Analysis settings" rows for the report-settings flyout, shown on every
// Skipper Report whatever its provenance. Rendered as the body of the "ANALYSIS SETTINGS"
// accordion section in the description column; the entry point back into the Skipper chat
// sits outside the section, so collapsing it does not take the entry point along.
const ReportPopupAnalysisSettings = ({ report }) => {
  const displayMd = readDisplayMd(report)
  const spec = readResolvedSpec(report)

  if (displayMd) {
    return (
      <div className={`${style['description-content-section-body-content-row']} ${style['analysis-plan']}`}>
        <Markdown>{displayMd}</Markdown>
      </div>
    )
  }

  if (hasResolvedSpec(spec)) return <ResolvedSpecRows spec={spec} />

  if (isAwaitingGeneration(report)) {
    return <div className={style['description-content-section-body-content-row']}>{ANALYSIS_SETTINGS_PENDING}</div>
  }

  return null
}

// real report -> modify_report; client-only draft/generating (negative id) -> create_report
const onChangeWithChat = report =>
  openGuidedReportsChat({
    entry: 'change',
    report: 0 < report.get('id') ? { name: report.get('title'), id: report.get('id') } : null
  })

// The way back into the Skipper chat for the settings above. It sits beside the section rather
// than inside it, and goes with the addon: a team whose addon reads as disabled cannot take
// the report back to the chat to change it. A team that can still buy the addon is offered the
// same paywall link the panel header shows, rather than a chat entry it cannot use.
export const ChangeWithChatButton = ({ report, features, isSurveyCompleted }) => {
  const feature = features?.[SKIPPER_REPORT_FEATURE]

  if (feature?.hidden) return null

  // `available` is read before `disabled` because the addon reports both while it is
  // purchasable — `disabled` alone would drop the upsell along with the locked state.
  if (feature?.available) {
    return (
      <div className={style['description-content-actions']}>
        <UnlockLink onPackage={feature.onPackage} isSurveyCompleted={isSurveyCompleted} />
      </div>
    )
  }

  if (feature?.disabled) return null

  return (
    <div className={style['description-content-actions']}>
      <Button
        className="p-button-stat p-button-rounded"
        label="Change with Skipper chat"
        icon={<i className="icon-aytmfd-skipper" />}
        onClick={() => onChangeWithChat(report)}
      />
    </div>
  )
}

ChangeWithChatButton.propTypes = {
  report: PropTypes.object,
  features: PropTypes.object,
  isSurveyCompleted: PropTypes.bool
}

ChangeWithChatButton.displayName = 'ChangeWithChatButton'

// The Skipper half of the description column: the settings section over the way back into the
// chat. Only a Skipper Report has either, and a locked addon that cannot be bought back has
// neither - so the whole block is withheld there.
export const ReportPopupSkipperSettings = ({ report, features, open, isSurveyCompleted, onToggle }) => {
  const feature = features?.[SKIPPER_REPORT_FEATURE]

  if (feature?.hidden || !isSkipperReport(report)) return null

  return (
    <>
      {feature?.disabled || !hasAnalysisSettings(report) ? null : (
        <ReportPopupSection title="Analysis settings" open={open} onToggle={onToggle}>
          <ReportPopupAnalysisSettings report={report} />
        </ReportPopupSection>
      )}
      <ChangeWithChatButton report={report} features={features} isSurveyCompleted={isSurveyCompleted} />
    </>
  )
}

ReportPopupSkipperSettings.propTypes = {
  report: PropTypes.object,
  features: PropTypes.object,
  open: PropTypes.bool,
  isSurveyCompleted: PropTypes.bool,
  onToggle: PropTypes.func
}

ReportPopupSkipperSettings.displayName = 'ReportPopupSkipperSettings'

ReportPopupAnalysisSettings.propTypes = {
  report: PropTypes.object
}

ReportPopupAnalysisSettings.displayName = 'ReportPopupAnalysisSettings'

export default ReportPopupAnalysisSettings
